import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ProfileService, Profile } from '../../services/profile.service';
import { AuthService } from '../../services/auth.service';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { DialogComponent, DialogConfig } from '../dialog/dialog.component';

@Component({
  selector: 'app-profile-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogComponent],
  templateUrl: './profile-settings.component.html',
  styleUrls: ['./profile-settings.component.scss']
})
export class ProfileSettingsComponent implements OnInit {
  profile: Profile | null = null;
  name: string = '';
  profilePictureUrl: string = '';
  selectedFile: File | null = null;
  previewUrl: string = '';
  isLoading: boolean = false;
  isSaving: boolean = false;

  // Timeout settings
  requestTimeoutMinutes: number = 30; // Varsayılan 30 dakika
  unansweredRequestMinutesThreshold: number = 3; // Varsayılan 3 dakika
  isLoadingTimeout: boolean = false;
  isSavingTimeout: boolean = false;
  timeoutError: string = '';

  // Working hours settings (Türkiye GMT+3)
  workingHoursStart: string = '09:00';
  workingHoursEnd: string = '22:00';
  isLoadingWorkingHours: boolean = false;
  isSavingWorkingHours: boolean = false;
  workingHoursError: string = '';
  errorMessage: string = '';
  showDialog: boolean = false;
  dialogConfig: DialogConfig = {
    title: '',
    message: '',
    type: 'info'
  };

  // Password change form
  showPasswordForm: boolean = false;
  currentPassword: string = '';
  newPassword: string = '';
  confirmPassword: string = '';
  isChangingPassword: boolean = false;
  passwordError: string = '';

  private readonly WORKING_HOURS_CACHE_KEY = 'lacivert_working_hours';

  constructor(
    private profileService: ProfileService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Çalışma saatlerini mümkün olduğunca hızlı göstermek için önce cache'den yükle
    this.loadWorkingHoursFromCache();

    this.loadProfile();
    this.loadTimeoutSettings();
    this.loadAutoResponseSettings();
    this.loadWorkingHours();
  }

  loadProfile(): void {
    this.isLoading = true;
    this.cdr.detectChanges(); // Loading state değişikliğini hemen yansıt
    this.profileService.getProfile()
      .pipe(
        finalize(() => {
          // Her durumda (başarılı veya hatalı) loading'i kapat
          this.isLoading = false;
          console.log('🟢 loadProfile tamamlandı, loading kapatıldı');
          this.cdr.detectChanges(); // Change detection'ı manuel tetikle
        })
      )
      .subscribe({
        next: (profile) => {
          console.log('✅ Profil başarıyla yüklendi:', profile);
          this.profile = profile;
          this.name = profile.name;
          this.profilePictureUrl = this.profileService.getProfilePictureUrl(profile.profile_picture_url || '');
        },
        error: (error) => {
          console.error('❌ Error loading profile:', error);
          console.error('❌ Hata detayları:', error.error);
          console.error('❌ HTTP Status:', error.status);
          this.errorMessage = error.error?.error || 'Profil bilgileri yüklenirken bir hata oluştu.';
        }
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.errorMessage = 'Lütfen bir resim dosyası seçin.';
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.errorMessage = 'Dosya boyutu 5MB\'dan küçük olmalıdır.';
        return;
      }

      this.selectedFile = file;
      this.errorMessage = '';

      // Create preview
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  uploadProfilePicture(): void {
    if (!this.selectedFile) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    this.profileService.uploadProfilePicture(this.selectedFile).subscribe({
      next: (response) => {
        this.profile = response.user;
        this.profilePictureUrl = this.profileService.getProfilePictureUrl(response.profile_picture_url);
        this.selectedFile = null;
        this.previewUrl = '';

        // Update auth service user
        const currentUser = this.authService.getCurrentUser();
        if (currentUser) {
          const updatedUser = { ...currentUser, profile_picture_url: response.profile_picture_url };
          this.authService.updateUser(updatedUser);
        }

        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error uploading profile picture:', error);
        this.errorMessage = error.error?.error || 'Profil fotoğrafı yüklenirken bir hata oluştu.';
        this.isSaving = false;
      }
    });
  }

  saveAll(): void {
    let hasError = false;
    this.errorMessage = '';
    this.timeoutError = '';
    this.workingHoursError = '';

    // Profile validation
    if (!this.name.trim()) {
      this.errorMessage = 'İsim alanı boş bırakılamaz.';
      hasError = true;
    }

    // Timeout validation
    if (this.requestTimeoutMinutes < 3 || this.requestTimeoutMinutes > 1440) {
      this.timeoutError = 'Timeout süresi 3 ile 1440 dakika arasında olmalıdır';
      hasError = true;
    }
    if (this.unansweredRequestMinutesThreshold < 1 || this.unansweredRequestMinutesThreshold > 1440) {
      this.timeoutError = 'Otomatik mesaj süresi 1 ile 1440 dakika arasında olmalıdır';
      hasError = true;
    }

    // Working hours validation
    const timeRegex = /^([01]?\d|2[0-3]):[0-5]\d$/;
    if (!this.workingHoursStart || !timeRegex.test(this.workingHoursStart)) {
      this.workingHoursError = 'Başlangıç saati HH:mm formatında olmalıdır (örn. 09:00)';
      hasError = true;
    }
    if (!this.workingHoursEnd || !timeRegex.test(this.workingHoursEnd)) {
      this.workingHoursError = 'Bitiş saati HH:mm formatında olmalıdır (örn. 22:00)';
      hasError = true;
    }
    if (!hasError) {
      const [sh, sm] = this.workingHoursStart.split(':').map(Number);
      const [eh, em] = this.workingHoursEnd.split(':').map(Number);
      const startMinutes = sh * 60 + sm;
      const endMinutes = eh * 60 + em;
      if (endMinutes <= startMinutes) {
        this.workingHoursError = 'Bitiş saati başlangıç saatinden sonra olmalıdır.';
        hasError = true;
      }
    }

    if (hasError) {
      this.showValidationDialog('Lütfen hataları düzeltin.');
      return;
    }

    this.isSaving = true;
    this.isSavingTimeout = true;
    this.isSavingWorkingHours = true;

    const profile$ = this.profileService.updateProfile(this.name.trim());
    const timeout$ = this.authService.put('/system/timeout-settings', {
      request_timeout_minutes: this.requestTimeoutMinutes
    });
    const autoResponse$ = this.authService.put('/system/auto-response-settings', {
      unanswered_request_minutes_threshold: this.unansweredRequestMinutesThreshold
    });
    const working$ = this.authService.put('/system/working-hours', {
      working_hours_start: this.workingHoursStart,
      working_hours_end: this.workingHoursEnd
    });

    forkJoin({ profile: profile$, timeout: timeout$, autoResponse: autoResponse$, working: working$ })
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.isSavingTimeout = false;
          this.isSavingWorkingHours = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: ({ profile }) => {
          console.log('✅ Ayarlar başarıyla güncellendi:', profile);
          this.profile = profile.user;
          this.name = profile.user.name;

          const currentUser = this.authService.getCurrentUser();
          if (currentUser) {
            const updatedUser: any = { ...currentUser, name: profile.user.name };
            if (profile.user.profile_picture_url) {
              updatedUser.profile_picture_url = profile.user.profile_picture_url;
            }
            this.authService.updateUser(updatedUser);
          }

          this.showSuccessDialog('Ayarlar başarıyla güncellendi.');
        },
        error: (error) => {
          console.error('❌ Ayarlar kaydedilirken hata:', error);
          let errorMsg = 'Ayarlar kaydedilirken bir hata oluştu.';
          if (error.error?.error) {
            errorMsg = error.error.error;
          } else if (error.error?.message) {
            errorMsg = error.error.message;
          } else if (error.message) {
            errorMsg = error.message;
          }
          this.errorMessage = errorMsg;
        }
      });
  }

  saveProfile(): void {
    if (!this.name.trim()) {
      this.errorMessage = 'İsim alanı boş bırakılamaz.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    this.profileService.updateProfile(this.name.trim()).subscribe({
      next: (response) => {
        console.log('✅ Profil başarıyla güncellendi:', response);
        this.profile = response.user;
        this.name = response.user.name; // Profil güncellendiğinde name'i de güncelle

        // Update auth service user
        const currentUser = this.authService.getCurrentUser();
        if (currentUser) {
          const updatedUser = { ...currentUser, name: response.user.name };
          this.authService.updateUser(updatedUser);
        }

        this.isSaving = false;
        this.cdr.detectChanges(); // Change detection'ı tetikle

        // Başarı dialog'u göster
        this.showSuccessDialog('Profil başarıyla güncellendi.');
      },
      error: (error) => {
        console.error('❌ Error updating profile:', error);
        console.error('❌ Error details:', error.error);
        console.error('❌ HTTP Status:', error.status);
        console.error('❌ Full error:', error);

        // Daha detaylı hata mesajı
        let errorMsg = 'Profil güncellenirken bir hata oluştu.';
        if (error.error?.error) {
          errorMsg = error.error.error;
        } else if (error.error?.message) {
          errorMsg = error.error.message;
        } else if (error.message) {
          errorMsg = error.message;
        }

        this.errorMessage = errorMsg;
        this.isSaving = false;
        this.cdr.detectChanges();
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/panel']);
  }

  getInitials(name: string): string {
    if (!name || name.trim().length === 0) {
      return 'U';
    }
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    // İlk kelimenin ilk harfi ve son kelimenin ilk harfi
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    // İsim soyisim baş harflerini kullan
    const fullName = this.profile?.name || this.name || 'User';
    const initials = this.getInitials(fullName);
    img.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=6366f1&color=fff&size=200&bold=true&length=2`;
    // Eğer bu da yüklenemezse, emoji göster
    img.onerror = () => {
      img.style.display = 'none';
      const emoji = document.createElement('div');
      emoji.textContent = '👤';
      emoji.style.fontSize = '80px';
      emoji.style.textAlign = 'center';
      emoji.style.lineHeight = '200px';
      img.parentElement?.appendChild(emoji);
    };
  }

  getDisplayPictureUrl(): string {
    if (this.previewUrl) {
      return this.previewUrl;
    }
    if (this.profilePictureUrl) {
      return this.profilePictureUrl;
    }
    // İsim soyisim baş harflerine göre dinamik avatar oluştur
    const fullName = this.profile?.name || this.name || 'User';
    const initials = this.getInitials(fullName);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=random&color=fff&size=200&bold=true&length=2`;
  }

  showSuccessDialog(message: string): void {
    this.dialogConfig = {
      title: 'Başarılı',
      message: message,
      type: 'success',
      confirmText: 'Tamam',
      showCancel: false
    };
    this.showDialog = true;
  }

  showValidationDialog(message: string): void {
    this.dialogConfig = {
      title: 'Lütfen kontrol edin',
      message: message,
      type: 'warning',
      confirmText: 'Tamam',
      showCancel: false
    };
    this.showDialog = true;
  }

  onDialogClose(): void {
    this.showDialog = false;
  }

  togglePasswordForm(): void {
    this.showPasswordForm = !this.showPasswordForm;
    if (this.showPasswordForm) {
      // Reset form when opening
      this.currentPassword = '';
      this.newPassword = '';
      this.confirmPassword = '';
      this.passwordError = '';
    }
  }

  changePassword(): void {
    // Validation
    if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
      this.passwordError = 'Tüm alanları doldurun';
      return;
    }

    if (this.newPassword.length < 6) {
      this.passwordError = 'Yeni şifre en az 6 karakter olmalıdır';
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.passwordError = 'Yeni şifreler eşleşmiyor';
      return;
    }

    this.isChangingPassword = true;
    this.passwordError = '';

    this.authService.changePassword(this.currentPassword, this.newPassword).subscribe({
      next: (response) => {
        console.log('✅ Şifre başarıyla değiştirildi');
        this.isChangingPassword = false;
        this.showPasswordForm = false;
        this.currentPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
        this.showSuccessDialog('Şifreniz başarıyla güncellendi');
      },
      error: (error) => {
        console.error('❌ Şifre değiştirme hatası:', error);
        this.isChangingPassword = false;
        this.passwordError = error.error?.error || 'Şifre değiştirilemedi';
      }
    });
  }

  // Timeout Settings Methods
  loadTimeoutSettings(): void {
    this.isLoadingTimeout = true;
    this.authService.get('/system/timeout-settings').subscribe({
      next: (response: any) => {
        this.requestTimeoutMinutes = response.request_timeout_minutes || 30;
        this.isLoadingTimeout = false;
        console.log('✅ Timeout ayarları yüklendi:', this.requestTimeoutMinutes);
      },
      error: (error) => {
        console.error('❌ Timeout ayarları yüklenirken hata:', error);
        this.isLoadingTimeout = false;
        this.timeoutError = 'Timeout ayarları yüklenemedi';
      }
    });
  }

  loadAutoResponseSettings(): void {
    this.isLoadingTimeout = true;
    this.authService.get('/system/auto-response-settings').subscribe({
      next: (response: any) => {
        this.unansweredRequestMinutesThreshold = response.unanswered_request_minutes_threshold || 3;
        this.isLoadingTimeout = false;
        console.log('✅ Otomatik mesaj ayarları yüklendi:', this.unansweredRequestMinutesThreshold);
      },
      error: (error) => {
        console.error('❌ Otomatik mesaj ayarları yüklenirken hata:', error);
        this.isLoadingTimeout = false;
        this.timeoutError = 'Otomatik mesaj ayarları yüklenemedi';
      }
    });
  }

  saveTimeoutSettings(): void {
    // Validation
    if (this.requestTimeoutMinutes < 3 || this.requestTimeoutMinutes > 1440) {
      this.timeoutError = 'Timeout süresi 3 ile 1440 dakika arasında olmalıdır';
      return;
    }
    if (this.unansweredRequestMinutesThreshold < 1 || this.unansweredRequestMinutesThreshold > 1440) {
      this.timeoutError = 'Otomatik mesaj süresi 1 ile 1440 dakika arasında olmalıdır';
      return;
    }

    this.isSavingTimeout = true;
    this.timeoutError = '';

    forkJoin({
      timeout: this.authService.put('/system/timeout-settings', {
        request_timeout_minutes: this.requestTimeoutMinutes
      }),
      autoResponse: this.authService.put('/system/auto-response-settings', {
        unanswered_request_minutes_threshold: this.unansweredRequestMinutesThreshold
      })
    })
      .pipe(
        finalize(() => {
          // Her durumda (başarılı veya hatalı) saving'i kapat
          this.isSavingTimeout = false;
          console.log('🟢 saveTimeoutSettings tamamlandı, saving kapatıldı');
          this.cdr.detectChanges(); // Change detection'ı manuel tetikle
        })
      )
      .subscribe({
        next: (response: any) => {
          console.log('✅ Timeout/otomatik mesaj ayarları güncellendi:', response);
          this.showSuccessDialog('Timeout ve otomatik mesaj süreleri başarıyla güncellendi.');
        },
        error: (error) => {
          console.error('❌ Timeout ayarları güncellenirken hata:', error);
          this.timeoutError = error.error?.error || 'Timeout/otomatik mesaj ayarları güncellenemedi';
        }
      });
  }

  // Working Hours (Çalışma Saatleri) Methods
  private loadWorkingHoursFromCache(): void {
    try {
      const cached = localStorage.getItem(this.WORKING_HOURS_CACHE_KEY);
      if (!cached) {
        return;
      }
      const parsed = JSON.parse(cached) as { working_hours_start?: string; working_hours_end?: string };
      if (parsed.working_hours_start) {
        this.workingHoursStart = parsed.working_hours_start;
      }
      if (parsed.working_hours_end) {
        this.workingHoursEnd = parsed.working_hours_end;
      }
      console.log('💾 Cache\'ten çalışma saatleri yüklendi:', this.workingHoursStart, this.workingHoursEnd);
    } catch (e) {
      console.warn('Çalışma saatleri cache okunamadı:', e);
    }
  }

  private saveWorkingHoursToCache(start: string, end: string): void {
    try {
      localStorage.setItem(
        this.WORKING_HOURS_CACHE_KEY,
        JSON.stringify({
          working_hours_start: start,
          working_hours_end: end
        })
      );
      console.log('💾 Çalışma saatleri cache\'e kaydedildi:', start, end);
    } catch (e) {
      console.warn('Çalışma saatleri cache yazılamadı:', e);
    }
  }

  // Working Hours (Çalışma Saatleri) Methods
  loadWorkingHours(): void {
    this.isLoadingWorkingHours = true;
    this.authService.get('/system/working-hours').subscribe({
      next: (response: any) => {
        this.workingHoursStart = response.working_hours_start || '09:00';
        this.workingHoursEnd = response.working_hours_end || '22:00';
        // En güncel çalışma saatlerini cache'e yaz
        this.saveWorkingHoursToCache(this.workingHoursStart, this.workingHoursEnd);
        this.isLoadingWorkingHours = false;
        console.log('✅ Çalışma saatleri yüklendi:', this.workingHoursStart, this.workingHoursEnd);
      },
      error: (error) => {
        console.error('❌ Çalışma saatleri yüklenirken hata:', error);
        this.isLoadingWorkingHours = false;
        this.workingHoursError = 'Çalışma saatleri yüklenemedi';
      }
    });
  }

  saveWorkingHours(): void {
    const timeRegex = /^([01]?\d|2[0-3]):[0-5]\d$/;
    if (!this.workingHoursStart || !timeRegex.test(this.workingHoursStart)) {
      this.workingHoursError = 'Başlangıç saati HH:mm formatında olmalıdır (örn. 09:00)';
      return;
    }
    if (!this.workingHoursEnd || !timeRegex.test(this.workingHoursEnd)) {
      this.workingHoursError = 'Bitiş saati HH:mm formatında olmalıdır (örn. 22:00)';
      return;
    }

    const [sh, sm] = this.workingHoursStart.split(':').map(Number);
    const [eh, em] = this.workingHoursEnd.split(':').map(Number);
    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;
    if (endMinutes <= startMinutes) {
      this.workingHoursError = 'Bitiş saati başlangıç saatinden sonra olmalıdır.';
      return;
    }

    this.isSavingWorkingHours = true;
    this.workingHoursError = '';

    this.authService.put('/system/working-hours', {
      working_hours_start: this.workingHoursStart,
      working_hours_end: this.workingHoursEnd
    })
      .pipe(
        finalize(() => {
          this.isSavingWorkingHours = false;
          console.log('🟢 saveWorkingHours tamamlandı, saving kapatıldı');
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: any) => {
          console.log('✅ Çalışma saatleri güncellendi:', response);
              // Kaydedilen çalışma saatlerini de cache'e yaz
              this.saveWorkingHoursToCache(this.workingHoursStart, this.workingHoursEnd);
          this.showSuccessDialog('Çalışma saatleri başarıyla güncellendi.');
        },
        error: (error) => {
          console.error('❌ Çalışma saatleri güncellenirken hata:', error);
          this.workingHoursError = error.error?.error || 'Çalışma saatleri güncellenemedi';
        }
      });
  }

  onWorkingHoursChange(): void {
    this.workingHoursError = '';
    const timeRegex = /^([01]?\d|2[0-3]):[0-5]\d$/;
    if (!this.workingHoursStart || !this.workingHoursEnd) {
      return;
    }
    if (!timeRegex.test(this.workingHoursStart) || !timeRegex.test(this.workingHoursEnd)) {
      return;
    }
    const [sh, sm] = this.workingHoursStart.split(':').map(Number);
    const [eh, em] = this.workingHoursEnd.split(':').map(Number);
    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;
    if (endMinutes <= startMinutes) {
      this.workingHoursError = 'Bitiş saati başlangıç saatinden sonra olmalıdır.';
    }
  }
}

